import React, { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import { Carousel } from "@domain/carousel/carousel";
import { useFavoriteCarousels } from "@domain/carousel/hooks";
import { FilterContentType } from "@domain/content/contentType";
import { Article } from "@domain/lms/article/article";
import { useFavoriteArticles } from "@domain/lms/article/hooks";
import { Course } from "@domain/lms/course/course";
import { useFavoriteCourses } from "@domain/lms/course/hooks";
import { TagsView } from "@ui/components/inputs/tagsView";
import { FavoriteArticlesList } from "./favoriteArticles";
import { FavoriteCarouselsList } from "./favoriteCarousels";
import { FavoriteCoursesList } from "./favoriteCourses";
import { FavoriteAllList, ProfileFavoriteContent } from "./favouriteAll.tx";

interface FavoriteListProps extends React.HTMLAttributes<HTMLDivElement> {
	userId: string;
}
const filterTypes: FilterContentType[] = ["COURSE", "ARTICLE", "CAROUSEL"];
export const FavoriteList: React.FC<FavoriteListProps> = ({ userId, ...htmlProps }) => {
	const {
		result: articles,
		fetchMore: fetchMoreArticles,
		loading: loadingArticles,
		moreLoading: moreLoadingArticles,
		totalElements: totalArticlesElements,
	} = useFavoriteArticles(userId);
	const {
		result: courses,
		fetchMore: fetchMoreCourses,
		loading: loadingCourses,
		moreLoading: moreLoadingCourses,
		totalElements: totalCoursesElements,
	} = useFavoriteCourses(userId);
	const {
		result: carousels,
		fetchMore: fetchMoreCarousels,
		loading: loadingCarousels,
		moreLoading: moreLoadingCarousels,
		totalElements: totalCarouselsElements,
	} = useFavoriteCarousels(userId);

	const [selectedFilter, setSelectedFilter] = useState(filterTypes[0]);
	const [mixedContent, setMixedContent] = useState(
		mixContent(articles ? articles : [], courses ? courses : [], carousels ? carousels : [])
	);

	useEffect(() => {
		if (
			!loadingArticles &&
			!loadingCourses &&
			!moreLoadingArticles &&
			!moreLoadingCourses &&
			!loadingCarousels &&
			!moreLoadingCarousels
		) {
			setMixedContent(mixContent(articles ? articles : [], courses ? courses : [], carousels ? carousels : []));
		}
	}, [
		loadingArticles,
		loadingCourses,
		moreLoadingCourses,
		moreLoadingArticles,
		loadingCarousels,
		moreLoadingCarousels,
	]);

	if (loadingArticles || loadingCourses || loadingCarousels) return <ClipLoader />;

	return (
		<>
			<TagsView
				tags={filterTypes}
				selectedTag={selectedFilter as string}
				onItemClick={(filter: string) => {
					setSelectedFilter(filter as FilterContentType);
				}}
			/>
			{selectedFilter === "ALL" && (
				<FavoriteAllList
					{...htmlProps}
					content={mixedContent}
					fetchMore={async () => {
						await fetchMoreCourses();
						await fetchMoreArticles();
						await fetchMoreCarousels();
					}}
					loading={loadingArticles && loadingCourses && loadingCarousels}
					moreLoading={moreLoadingArticles && moreLoadingCourses && moreLoadingCarousels}
					totalElements={(totalArticlesElements || 0) + (totalCoursesElements || 0) + (totalCarouselsElements || 0)}
				/>
			)}
			{selectedFilter === "COURSE" && (
				<FavoriteCoursesList
					{...htmlProps}
					courses={courses}
					fetchMore={fetchMoreCourses}
					loading={loadingCourses}
					moreLoading={moreLoadingCourses}
					totalElements={totalCoursesElements}
				/>
			)}
			{selectedFilter === "ARTICLE" && (
				<FavoriteArticlesList
					{...htmlProps}
					articles={articles}
					fetchMore={fetchMoreArticles}
					loading={loadingArticles}
					moreLoading={moreLoadingArticles}
					totalElements={totalArticlesElements}
				/>
			)}
			{selectedFilter === "CAROUSEL" && (
				<FavoriteCarouselsList
					{...htmlProps}
					carousels={carousels}
					fetchMore={fetchMoreCarousels}
					loading={loadingCarousels}
					moreLoading={moreLoadingCarousels}
					totalElements={totalCarouselsElements}
				/>
			)}
		</>
	);
};

const mixContent = (articles: Article[], courses: Course[], carousels: Carousel[]): ProfileFavoriteContent[] => {
	const mixedContent: ProfileFavoriteContent[] = [];
	const contentLength: number = articles.length + courses.length + carousels.length;
	let i = 0;
	while (i < contentLength) {
		if (i < articles.length) {
			mixedContent.push(articles[i]);
		}
		if (i < courses.length) {
			mixedContent.push(courses[i]);
		}

		if (i < carousels.length) {
			mixedContent.push(carousels[i]);
		}
		i++;
	}
	return mixedContent;
};
